/**
 * Subscription Widget  –  section-subscription-widget.js
 *
 * Handles subscribe vs one-time toggle UI, price updates, and — critically —
 * the subscription cart-add logic via a window.fetch patch.
 *
 * WHY fetch-patch instead of intercepting the form submit / button click:
 *   Fast Bundle registers its own <product-form> custom element before
 *   product-form.js loads, so our customElements.define() guard skips our
 *   entire product-form.js. UpCart also intercepts form submit events at the
 *   document capture-phase. Both apps prevent our code from running.
 *
 *   The fetch patch is the only reliable interception point that:
 *     1. Runs AFTER UpCart/Fast Bundle have already patched window.fetch
 *        (they patch on script load; our patch runs on connectedCallback)
 *     2. Fires for EVERY cart/add call regardless of what triggered it
 *     3. Is app-agnostic — UpCart, Fast Bundle, etc. all call window.fetch
 *
 * HOW it works:
 *   When the widget connects, it wraps window.fetch.
 *   On every POST to /cart/add, if selectedMode === 'subscribe' and a
 *   filterVariantId is configured, the filter item is injected into the
 *   items array before the request leaves the browser.
 *   If the filter item is already in the request (e.g. when our own
 *   product-form.js also runs), it is NOT added again.
 */

if (!customElements.get('subscription-widget')) {
  customElements.define(
    'subscription-widget',
    class SubscriptionWidget extends HTMLElement {

      constructor() {
        super();
        this.selectedMode = 'subscribe';
        this._originalFetch = null;
      }

      connectedCallback() {
        this._bindOptions();
        this._syncWithVariantPicker();
        this._patchFetch();
      }

      disconnectedCallback() {
        this._unpatchFetch();
      }

      /* ─── option selection ────────────────────────────────── */

      _bindOptions() {
        this.querySelectorAll('[data-sub-option]').forEach((opt) => {
          opt.addEventListener('click', this._pickOption.bind(this));
          opt.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this._pickOption(e);
            }
          });
        });
      }

      _pickOption(evt) {
        const target = evt.currentTarget;
        this.selectedMode = target.dataset.subOption;
        console.log('[SUB] Mode changed to:', this.selectedMode);
        this.querySelectorAll('[data-sub-option]').forEach((opt) => {
          const active = opt === target;
          opt.classList.toggle('sub-widget__option--active', active);
          opt.setAttribute('aria-checked', String(active));
        });
      }

      /* ─── fetch patch ─────────────────────────────────────── */

      _patchFetch() {
        if (this._originalFetch) return; // already patched
        this._originalFetch = window.fetch;
        const widget = this;

        window.fetch = function (input, init) {
          init = init || {};

          // Only intercept /cart/add calls
          const urlStr = (typeof input === 'string' ? input : (input && input.url)) || '';
          const isCartAdd = urlStr.includes('/cart/add');

          if (!isCartAdd) {
            return widget._originalFetch.call(this, input, init);
          }

          console.log('[SUB][PATCH] Intercepted cart/add call. selectedMode:', widget.selectedMode);
          console.log('[SUB][PATCH] filterVariantId on widget:', widget.dataset.filterVariantId);

          // Only inject when in subscribe mode with a valid filter variant
          const filterVariantId = (widget.dataset.filterVariantId || '').trim();
          if (widget.selectedMode !== 'subscribe' || !filterVariantId) {
            console.log('[SUB][PATCH] Passing through unchanged (mode=' + widget.selectedMode + ', filterId=' + filterVariantId + ')');
            return widget._originalFetch.call(this, input, init);
          }

          const filterItem = {
            id: parseInt(filterVariantId, 10),
            quantity: 1,
            properties: { _auto_added: 'subscription_filter' },
          };
          const filterSellingPlanId = (widget.dataset.filterSellingPlanId || '').trim();
          if (filterSellingPlanId) {
            filterItem.selling_plan = parseInt(filterSellingPlanId, 10);
          }

          let modifiedInit;

          if (init.body instanceof FormData) {
            // ── FormData → convert to JSON so we can send multiple items ──
            const variantId = init.body.get('id');
            const qty       = parseInt(init.body.get('quantity') || 1, 10);

            if (!variantId) {
              console.warn('[SUB][PATCH] FormData has no id field — passing through');
              return widget._originalFetch.call(this, input, init);
            }

            const mainItem = { id: parseInt(variantId, 10), quantity: qty };

            // Don't double-add the filter if it's already present
            if (mainItem.id === filterItem.id) {
              console.log('[SUB][PATCH] mainItem IS the filter — passing through unchanged');
              return widget._originalFetch.call(this, input, init);
            }

            const body = { items: [mainItem, filterItem] };

            // Preserve sections / sections_url if present
            const sections    = init.body.get('sections');
            const sectionsUrl = init.body.get('sections_url');
            if (sections)    body.sections     = sections.split(',');
            if (sectionsUrl) body.sections_url = sectionsUrl;

            const existingHeaders = init.headers
              ? (init.headers instanceof Headers
                  ? Object.fromEntries(init.headers.entries())
                  : { ...init.headers })
              : {};

            modifiedInit = Object.assign({}, init, {
              headers: Object.assign({}, existingHeaders, {
                'Content-Type': 'application/json',
                'Accept':       'application/json',
              }),
              body: JSON.stringify(body),
            });

            console.log('[SUB][PATCH] Converted FormData → JSON. Items:', JSON.stringify(body.items));

          } else if (typeof init.body === 'string') {
            // ── JSON body ──
            let parsed;
            try {
              parsed = JSON.parse(init.body);
            } catch (e) {
              console.warn('[SUB][PATCH] Could not parse JSON body — passing through');
              return widget._originalFetch.call(this, input, init);
            }

            if (parsed.items && Array.isArray(parsed.items)) {
              // Check if filter already in items (our product-form.js may have added it)
              const alreadyHasFilter = parsed.items.some(
                (item) => parseInt(item.id, 10) === parseInt(filterVariantId, 10)
              );
              if (alreadyHasFilter) {
                console.log('[SUB][PATCH] Filter already in items array — passing through unchanged');
                return widget._originalFetch.call(this, input, init);
              }
              parsed.items.push(filterItem);
            } else if (parsed.id) {
              // Single-item JSON format → upgrade to items array
              const mainItem = { id: parseInt(parsed.id, 10), quantity: parsed.quantity || 1 };
              if (mainItem.id === filterItem.id) {
                console.log('[SUB][PATCH] mainItem IS the filter — passing through unchanged');
                return widget._originalFetch.call(this, input, init);
              }
              parsed.items = [mainItem, filterItem];
              delete parsed.id;
              delete parsed.quantity;
            } else {
              console.warn('[SUB][PATCH] Unrecognised JSON body shape — passing through');
              return widget._originalFetch.call(this, input, init);
            }

            modifiedInit = Object.assign({}, init, { body: JSON.stringify(parsed) });
            console.log('[SUB][PATCH] Injected filter item into JSON body. items:', JSON.stringify(parsed.items));

          } else {
            // Unknown body type — pass through
            console.warn('[SUB][PATCH] Unknown body type (' + typeof init.body + ') — passing through');
            return widget._originalFetch.call(this, input, init);
          }

          return widget._originalFetch.call(this, input, modifiedInit);
        };

        console.log('[SUB] window.fetch patched ✔');
      }

      _unpatchFetch() {
        if (this._originalFetch) {
          window.fetch = this._originalFetch;
          this._originalFetch = null;
          console.log('[SUB] window.fetch restored');
        }
      }

      /* ─── variant change → update displayed prices ────────── */

      _syncWithVariantPicker() {
        if (typeof subscribe === 'undefined' || !window.PUB_SUB_EVENTS) return;
        subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
          if (!data || !data.variant) return;
          if (data.sectionId && data.sectionId !== this.dataset.sectionId) return;

          const variant = data.variant;
          this.dataset.variantId = variant.id;

          const discountPct    = parseInt(this.dataset.discount || 20, 10);
          const regularCents   = variant.price;
          const discountCents  = Math.round(regularCents * discountPct / 100);
          const subscribeCents = regularCents - discountCents;

          const subEl = this.querySelector('[data-sub-price]');
          const cmpEl = this.querySelector('[data-sub-compare]');
          const oneEl = this.querySelector('[data-onetime-price]');

          if (subEl) subEl.textContent = this._money(subscribeCents);
          if (cmpEl) cmpEl.textContent = this._money(regularCents);
          if (oneEl) oneEl.textContent = this._money(regularCents);
        });
      }

      /* ─── money formatter ─────────────────────────────────── */

      _money(cents) {
        if (window.Shopify && window.Shopify.formatMoney) {
          return window.Shopify.formatMoney(cents);
        }
        const amount   = (cents / 100).toFixed(2);
        const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
        return currency ? `${currency} ${amount}` : amount;
      }
    }
  );
}
